import { LoaderFunctionArgs, redirect } from 'react-router';
import { getSession, requireUser, createAuthenticatedFetch } from './session.server';
import { handleApiError, logError } from './errors';

/**
 * Loader for protected routes that require authentication
 */
export async function protectedLoader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const api = createAuthenticatedFetch();
  
  return {
    user,
    api,
  };
}

/**
 * Loader for auth routes (login, register) that redirects if already logged in
 */
export async function authLoader(): Promise<Response | null> {
  const session = await getSession();
  
  if (session.user) {
    // Already logged in, redirect to dashboard
    return redirect('/dashboard');
  }
  
  return null;
}


/**
 * Dashboard loader with user data and stats
 */
export async function dashboardLoader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const api = createAuthenticatedFetch();
  
  try {
    // Check onboarding status first
    const onboardingResponse = await api.get('/onboarding/status').catch(() => null);
    if (onboardingResponse?.data && !onboardingResponse.data.completed) {
      // Redirect to onboarding if not completed
      throw redirect('/onboarding');
    }
    
    // Fetch dashboard data in parallel
    const [stats, recentJobs, applications] = await Promise.all([
      api.get('/user/stats').catch(() => ({ data: null })),
      api.get('/jobs/recent').catch(() => ({ data: [] })),
      api.get('/applications/recent').catch(() => ({ data: [] })),
    ]);
    
    return {
      user,
      stats: stats.data || {
        activeApplications: 0,
        savedJobs: 0,
        profileViews: 0,
      },
      recentJobs: recentJobs.data || [],
      applications: applications.data || [],
    };
  } catch (error) {
    // Handle redirect errors by re-throwing
    if (error instanceof Response) {
      throw error;
    }
    
    logError(error, { context: 'dashboardLoader' });
    // Return default data instead of throwing to keep the UI functional
    return {
      user,
      stats: {
        activeApplications: 0,
        savedJobs: 0,
        profileViews: 0,
      },
      recentJobs: [],
      applications: [],
      error: 'Failed to load dashboard data',
    };
  }
}

/**
 * Job search loader
 */
export async function jobSearchLoader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const api = createAuthenticatedFetch();
  const url = new URL(request.url);
  
  const query = url.searchParams.get('q') || '';
  const location = url.searchParams.get('location') || '';
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  
  if (!query && !location) {
    return {
      user,
      jobs: [],
      query: '',
      location: '',
      page: 1,
      totalPages: 0,
    };
  }
  
  try {
    const response = await api.get('/jobs/search', {
      params: { q: query, location, page },
    });
    
    return {
      user,
      jobs: response.data?.jobs || [],
      query,
      location,
      page,
      totalPages: response.data?.totalPages || 0,
    };
  } catch (error) {
    logError(error, { context: 'jobSearchLoader', query, location });
    // For API errors, we might want to throw to show error boundary
    if (error instanceof Error && 'status' in error) {
      handleApiError(error);
    }
    // Return empty results with error message for other errors
    return {
      user,
      jobs: [],
      query,
      location,
      page,
      totalPages: 0,
      error: 'Failed to search jobs. Please try again.',
    };
  }
}